import { useEffect, useRef } from "react";
import { features } from "@/mocks/products";

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-charcoal-800 py-24 md:py-32">
      <div className="w-full px-6 md:px-10 lg:px-16">
        {/* Section Title */}
        <div className="text-center mb-16 md:mb-20">
          <h2 className="reveal opacity-0 translate-y-4 transition-all duration-600 ease-out font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-charcoal-50 leading-tight">
            Why Propre Purification
          </h2>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="reveal opacity-0 translate-y-6 transition-all duration-700 ease-out rounded-2xl overflow-hidden relative aspect-square group cursor-default border border-dark-border"
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Background Image */}
              <img
                src={feature.image}
                alt={feature.title}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/90 via-dark-bg/50 to-dark-bg/20" />

              {/* Text Content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 md:p-8">
                <h3 className="font-serif text-xl md:text-2xl font-semibold text-charcoal-50 mb-3 leading-tight">
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-charcoal-200/90 font-sans leading-relaxed max-w-xs">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}